import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, User, LogOut, Wallet, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './auth/LoginModal';
import RegisterModal from './auth/RegisterModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [openForgotOnLogin, setOpenForgotOnLogin] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  // Check for login/register requirement from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for login parameter
    if (urlParams.get('login') === 'required' && !isAuthenticated) {
      setShowLogin(true);
      // Clean up the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
    
    // Coming back from an expired reset link: open the forgot-password step
    if (urlParams.get('forgot') === '1' && !isAuthenticated) {
      setOpenForgotOnLogin(true);
      setShowLogin(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check for register parameter
    if (urlParams.get('register') === 'true' && !isAuthenticated) {
      setShowRegister(true);
      // Clean up the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [isAuthenticated]);

  const handleGetQuote = () => {
    window.location.href = '/quote';
  };

  const handleLogout = () => {
    logout();
    // Redirect to homepage after logout
    window.location.href = '/';
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-100">
        {/* Top Bar */}
        <div className="bg-slate-900 text-white py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Phone className="h-3 w-3" />
                  <span>011-47501136</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-3 w-3" />
                  <span>info@xfas.in</span>
                </div>
                <a
                  href="https://wa.me/919821984141"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 hover:text-orange-400 transition-colors"
                >
                  <Phone className="h-3 w-3" />
                  <span>WhatsApp 9821984141</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => window.location.href = '/'}>
              <img
                src="/assets/images/xfas-logo.png"
                alt="XFas Logistics"
                className="h-10 w-auto"
              />
              <div className="ml-3">
                <h1 className="text-xl font-bold text-slate-900">XFas Logistics</h1>
                <p className="text-xs text-gray-500">Multi-Channel Shipping Solutions</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {/* Destinations dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 text-gray-700 hover:text-orange-500 font-medium transition-colors">
                  Destinations <ChevronDown className="h-4 w-4" />
                </button>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <a href="/send-courier-from-india-to-usa/" className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                    🇺🇸 India to USA
                  </a>
                  <a href="/send-courier-from-india-to-uk/" className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                    🇬🇧 India to UK
                  </a>
                  <a href="/send-courier-from-india-to-europe/" className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                    🇪🇺 India to Europe
                  </a>
                </div>
              </div>
              <a href="/track/" className="text-gray-700 hover:text-orange-500 font-medium transition-colors">
                Track Shipment
              </a>
              {isAuthenticated && (
                <a href="/dashboard" className="text-gray-700 hover:text-orange-500 font-medium transition-colors">
                  Dashboard
                </a>
              )}
              <a href="/support/" className="text-gray-700 hover:text-orange-500 font-medium transition-colors">
                Support
              </a>
              <a href="/about/" className="text-gray-700 hover:text-orange-500 font-medium transition-colors">
                About
              </a>
              <a href="/blog/" className="text-gray-700 hover:text-orange-500 font-medium transition-colors">
                Blog
              </a>
            </nav>

            {/* Auth Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-50">
                      <User className="h-4 w-4 mr-2" />
                      {user?.first_name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => window.location.href = '/dashboard'}>
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.location.href = '/profile'}>
                      Profile & Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.location.href = '/my-shipments'}>
                      My Shipments
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.location.href = '/wallet'}>
                      <Wallet className="h-4 w-4 mr-2" />
                      My Wallet
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="border-orange-500 text-orange-500 hover:bg-orange-50"
                    onClick={() => setShowLogin(true)}
                  >
                    Login
                  </Button>
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => setShowRegister(true)}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
                <div className="px-3 py-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Destinations</p>
                  <a href="/send-courier-from-india-to-usa/" className="block py-1.5 text-gray-700 hover:text-orange-500 font-medium text-sm">🇺🇸 India to USA</a>
                  <a href="/send-courier-from-india-to-uk/" className="block py-1.5 text-gray-700 hover:text-orange-500 font-medium text-sm">🇬🇧 India to UK</a>
                  <a href="/send-courier-from-india-to-europe/" className="block py-1.5 text-gray-700 hover:text-orange-500 font-medium text-sm">🇪🇺 India to Europe</a>
                </div>
                <a href="#track"
                  className="block px-3 py-2 text-gray-700 hover:text-orange-500 font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = '/track';
                  }}
                >
                  Track Shipment
                </a>
                <a href="/support/" className="block px-3 py-2 text-gray-700 hover:text-orange-500 font-medium">
                  Support
                </a>
                <a href="/about/" className="block px-3 py-2 text-gray-700 hover:text-orange-500 font-medium">
                  About
                </a>
                <a href="/blog/" className="block px-3 py-2 text-gray-700 hover:text-orange-500 font-medium">
                  Blog
                </a>

                {/* Mobile Auth */}
                <div className="px-3 py-2 space-y-2">
                  {isAuthenticated ? (
                    <>
                      <div className="text-gray-900 font-medium">
                        Welcome, {user?.first_name}!
                      </div>
                      <Button
                        variant="outline"
                        className="w-full mb-2"
                        onClick={() => {
                          window.location.href = '/dashboard';
                          setIsMenuOpen(false);
                        }}
                      >
                        Dashboard
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full mb-2"
                        onClick={() => {
                          window.location.href = '/profile';
                          setIsMenuOpen(false);
                        }}
                      >
                        Profile
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full mb-2"
                        onClick={() => {
                          window.location.href = '/my-shipments';
                          setIsMenuOpen(false);
                        }}
                      >
                        My Shipments
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full mb-2"
                        onClick={() => {
                          window.location.href = '/wallet';
                          setIsMenuOpen(false);
                        }}
                      >
                        <Wallet className="h-4 w-4 mr-2" />
                        My Wallet
                      </Button>
                      <Button variant="outline" className="w-full" onClick={handleLogout}>
                        Logout
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        className="w-full border-orange-500 text-orange-500 hover:bg-orange-50"
                        onClick={() => {
                          setShowLogin(true);
                          setIsMenuOpen(false);
                        }}
                      >
                        Login
                      </Button>
                      <Button
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={() => {
                          setShowRegister(true);
                          setIsMenuOpen(false);
                        }}
                      >
                        Sign Up
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Auth Modals */}
      <LoginModal
        isOpen={showLogin}
        openForgotPassword={openForgotOnLogin}
        onClose={() => {
          setShowLogin(false);
          setOpenForgotOnLogin(false);
        }}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
      />

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSwitchToLogin={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />
    </>
  );
};

export default Header;